import { useEffect, useState, type FormEvent } from 'react';
import {
  Link,
  Navigate,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthBrandHeading } from '@/auth/auth-brand-heading';
import { Label } from '@/components/ui/label';
import { CheckCircle2 } from 'lucide-react';
import { useAuthStore } from '@/auth/auth-store';
import {
  ApiAuthError,
  setPassword,
  userDataLoginWithCode,
  type SignUpUserData,
} from '@/auth/auth-api';
import { isBlank } from '@/auth/email-validation';
import { safeLogAuthError } from '@/auth/log-helpers';
import {
  PASSWORD_RULES,
  allRulesPass,
  evaluateRules,
  passwordsMatch,
} from '@/auth/password-validation';

/**
 * Create Password (via email link) — Phase 4 of PROD-891.
 *
 * Flutter source:
 *   - `create_password/presentation/pages/create_password_screen.dart`
 *   - `create_password/presentation/bloc/create_password/create_password_bloc.dart`
 *   - `create_password/data/datasource/create_password_data_source_impl.dart`
 *
 * Entry: external email link with `?email=<email>&token=<resetToken>` query
 * params. On mount, fetches the user's profile via `userDataLoginWithCode`
 * (Flutter `GetUserData` event at `:42-52` fires on initState) so the page
 * can show a personalised greeting + photo. Form is the same 5-rule
 * validator + match shape as Phase 3b — both use the shared
 * `password-validation.ts` helper.
 *
 * On submit: POST `/api/Account/SetPassword` body
 * `{email, password, confirmPassword === password, passwordResetToken}`
 * — Flutter `create_password_data_source_impl.dart:24-40`. Reuses
 * `setPassword` wrapper from Phase 3.
 *
 * On success → `/password-changed` (Phase 6 route; falls back to catch-all
 * until Phase 6 lands).
 *
 * **Query-param injection safety (Samer review #317)**:
 *   - `searchParams.get(...)` returns the **already-URL-decoded** string per
 *     the WHATWG URL spec — we receive `email = "user@scrips.com"`, not
 *     `"user%40scrips.com"`.
 *   - The decoded email is then passed to the `userDataLoginWithCode`
 *     wrapper, which re-applies `encodeURIComponent(email)` before
 *     interpolating into the URL path (`auth-api.ts:userDataLoginWithCode`).
 *     Special characters (`/`, `?`, `#`, spaces, etc.) are percent-encoded
 *     before the fetch call.
 *   - For the SetPassword POST body, `JSON.stringify` handles escaping;
 *     no path-segment injection surface there.
 *   - `passwordResetToken` is passed verbatim into a JSON body — same
 *     `JSON.stringify` safety.
 *
 * **Token validity (Samer review #317)**:
 *   The Flutter endpoint `userDataLoginWithCode` accepts only `email` in
 *   the URL path — there is NO token argument. So we cannot pre-validate
 *   the reset token before showing the form. Token validity is confirmed
 *   only at SetPassword time. If it's expired/used, the catch block below
 *   surfaces a user-actionable "request a new link" message rather than
 *   the generic Flutter "Failed to create password" toast. Eager
 *   pre-validation requires a backend change (either extend
 *   UserDataLoginWithCode to accept + verify the token, or add a new
 *   `/api/Account/ValidateResetToken` endpoint) — logged in
 *   orchestration-log as a backend follow-up.
 */
export function CreatePasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);

  const email = searchParams.get('email') ?? '';
  const passwordResetToken = searchParams.get('token') ?? '';

  const [password, setPwd] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState<SignUpUserData | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  useEffect(() => {
    if (!email) {
      setIsLoadingProfile(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await userDataLoginWithCode(email);
        if (!cancelled) setUserData(data);
      } catch (err) {
        // Silent on profile-fetch failure — user can still set their
        // password; the form just doesn't show a personalised greeting.
        safeLogAuthError('Create password — user data', err);
      } finally {
        if (!cancelled) setIsLoadingProfile(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [email]);

  if (status === 'authenticated' || status === 'refreshing') {
    return <Navigate to="/" replace />;
  }

  // Direct URL hit without email + token query params → there's nothing
  // to do. Bounce to login so the user can request a fresh link.
  //
  // **Samer review #317**: `isBlank` (Flutter parity) catches both empty
  // strings AND whitespace-only values. Naive `!email` only catches
  // empty/null/undefined. A pasted URL like `?email=%20%20&token=abc`
  // would otherwise reach the password form with a blank email mirror
  // and hit a server-side error at SetPassword time. Bail early.
  if (isBlank(email) || isBlank(passwordResetToken)) {
    return <Navigate to="/login" replace />;
  }

  const ruleResults = evaluateRules(password);
  const allPass = allRulesPass(password);
  const match = passwordsMatch(password, confirmPassword);
  const showMismatch = !!confirmPassword && !match;
  const canSubmit = allPass && match && !isSubmitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await setPassword({ email, password, passwordResetToken });
      navigate('/password-changed', { replace: true });
    } catch (err) {
      safeLogAuthError('Create password — submit', err);
      // **Samer review #317** — expired/already-consumed token feedback.
      //
      // The Flutter endpoint signals token failure via 400 / 401 from
      // SetPassword (`create_password_data_source_impl.dart:24-40`
      // surfaces these as `Failure('Failed to create password')` —
      // Flutter does NOT differentiate). Per CLAUDE.md Dart-source
      // rule we ship the same UX shape (toast + stay on page), but
      // expand the message on 4xx so the user understands their reset
      // link is the problem rather than the password itself. The
      // user can click "Log in" to navigate back and request a fresh
      // link from /forgot-password.
      //
      // Eager pre-validation of the token isn't possible against this
      // endpoint — `userDataLoginWithCode(email)` accepts only the
      // email (Flutter `:43-56`). Token validity is only confirmable
      // at SetPassword time. A backend change to either (a) have
      // UserDataLoginWithCode accept + validate the token or (b) add
      // a lightweight `/api/Account/ValidateResetToken` endpoint
      // would unblock eager validation; logged as backend follow-up
      // in orchestration-log.
      if (err instanceof ApiAuthError && err.status >= 400 && err.status < 500) {
        toast.error(
          'Your reset link has expired or already been used. Click "Log in" and request a new one.',
        );
      } else {
        toast.error('Failed to create password');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const greeting = userData?.firstName
    ? `Welcome, ${userData.firstName}`
    : 'Create your password';

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sidebar px-4">
      <form
        onSubmit={onSubmit}
        className="w-90 max-w-full rounded-lg border border-border-default bg-surface-card p-8 flex flex-col gap-4"
        aria-label="Create your password"
      >
        {userData?.photoUrl && (
          <img
            src={userData.photoUrl}
            alt=""
            className="w-12 h-12 rounded-full object-cover"
          />
        )}
        <AuthBrandHeading
          title={isLoadingProfile ? 'Loading…' : greeting}
          subtitle={userData?.emailAddress || email}
        />

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="create-password"
            className="text-xs font-medium text-text-secondary"
          >
            Create password
          </Label>
          <Input
            id="create-password"
            type="password"
            autoComplete="new-password"
            placeholder="Create password"
            value={password}
            onChange={(e) => setPwd(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        {allPass ? (
          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-status-success-text)]">
            <CheckCircle2 className="h-4 w-4" />
            Your password is secure
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm font-semibold">
            {PASSWORD_RULES.map((rule, i) => (
              <span
                key={rule.label}
                className={
                  ruleResults[i]
                    ? 'text-[var(--color-status-success-text)]'
                    : 'text-text-muted'
                }
              >
                {rule.label}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="create-confirm-password"
            className="text-xs font-medium text-text-secondary"
          >
            Confirm password
          </Label>
          <Input
            id="create-confirm-password"
            type="password"
            autoComplete="new-password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirm(e.target.value)}
            aria-invalid={showMismatch || undefined}
            aria-describedby={
              showMismatch ? 'create-confirm-password-error' : undefined
            }
            disabled={isSubmitting}
          />
          {showMismatch && (
            <span
              id="create-confirm-password-error"
              role="alert"
              className="text-sm text-status-error"
            >
              Password not matching
            </span>
          )}
        </div>

        <Button type="submit" disabled={!canSubmit} className="mt-2">
          {isSubmitting ? 'Saving…' : 'Save password'}
        </Button>

        <Link
          to="/login"
          className="self-center text-sm font-medium text-brand-primary hover:underline"
        >
          Log in
        </Link>
      </form>
    </div>
  );
}

export default CreatePasswordPage;
